import { useAppDispatch, useAppSelector } from 'hooks/redux';
import type { ReactNode } from 'react';
// CORE
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useRef } from 'react';
import { useEffectOnce } from 'react-use';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// MUI
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { useTheme, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

// APP-COMPONENTS
import {
  aggErrors,
  aggErrorsSelectors,
  fiMigrationUtils,
  type accountDiscoveryTypes,
} from 'app-components/flux/account-discovery';
import type { accountsTypes } from 'app-components/flux/accounts';
import {
  institutionLoginsActions,
  institutionLoginsSelectors,
  institutionLoginsUtils,
} from 'app-components/flux/institution-logins';
import type { InstitutionLogin } from 'app-components/flux/institution-logins/institutionLoginsTypes';
import { institutionsSelectors } from 'app-components/flux/institutions';
import type { FiIssue } from 'app-components/flux/institutions/institutionsTypes';
import { tracker } from 'app-components/utils/core';

// LOCAL
import A2AEntryButton from 'components/A2AEntryButton';
import showManageUpdatesDialog from 'components/Dialogs/ManageUpdatesDialog/dispatcher';

type AggAction = accountDiscoveryTypes.AggAction;

const alertStyles = {
  display: 'flex',
  alignItems: 'flex-start',
  color: (theme: Theme) => theme.palette.text.primary,
  border: (theme: Theme) => `1px solid ${theme.palette.border.light}`,
  '& .MuiAlert-action': {
    marginRight: 0,
    paddingTop: 0,
    alignSelf: 'center',
  },
  '& .MuiAlert-icon': {
    padding: 0,
  },
  '& .MuiAlert-message': {
    padding: 0,
  },
};

const WarningIcon = <WarningRoundedIcon sx={{ color: (theme) => theme.palette.warning.main }} />;

type InstitutionLoginStatusBannerProps = {
  institutionLoginId: string;
  /** Used for mixpanel tracking */
  location: 'account settings' | 'account list' | 'investments';
  /** Should we show both the fi issue & fi error if they both exist. Will automatically show both if issue is of type INFO*/
  showBoth: boolean;
  /** Add styles to the root element */
  className?: string;
  /** Is the status banner dismissible */
  dismissible?: boolean;
  /** Can we show the A2A entry point */
  showA2AEntry?: boolean;
  /** Hide the A2A entry point when account is A2A verified */
  hideA2AOnVerified?: boolean;
  /** Only show the A2A entry point when account is A2A verified */
  onlyA2AOnVerified?: boolean;
  /** The account we enter for the A2A entery point */
  account?: accountsTypes.Account;
  /** The dedicated connection update banner owns this optional action; retain real FI notices. */
  hideMigrationAction?: boolean;
};

const FiIssueBody = ({ fiIssue }: { fiIssue: FiIssue }) => {
  return (
    <>
      <AlertTitle variant='paragraphMedium'>{fiIssue.shortMessage}</AlertTitle>
      <Typography variant='paragraphRegular' color='text.secondary'>
        {fiIssue.longMessage}
      </Typography>
    </>
  );
};

const FiErrorBody = ({ aggAction }: { aggAction: AggAction }) => {
  return (
    <>
      <Stack direction='row' gap={1} alignItems='center'>
        <Typography variant='paragraphMedium'>{aggAction.title}</Typography>
        {aggAction.statusCode || aggAction.code ? (
          <Typography variant='captionRegular' sx={{ fontFamily: 'monospace' }} color='text.secondary'>
            {`(Care code: ${aggAction.statusCode || aggAction.code})`}
          </Typography>
        ) : null}
      </Stack>
      <Typography variant='paragraphRegular' color='text.secondary'>
        {aggAction.text}
      </Typography>
    </>
  );
};

const FiLoginActionButton = ({
  institutionLogin,
  fiIssue,
  aggAction,
  location,
  trackAction,
  hideAction = false,
}: {
  institutionLogin: InstitutionLogin;
  fiIssue?: FiIssue;
  aggAction: AggAction;
  location: string;
  trackAction: (action: 'close' | 'refresh' | 'shown') => void;
  hideAction?: boolean;
}) => {
  const dispatch = useAppDispatch();

  const aggLoading = useAppSelector((state) =>
    institutionLoginsSelectors.getIdsRefreshing(state)?.includes(institutionLogin.id),
  );

  const kickoffReconnect = () => {
    if (fiIssue) {
      trackAction('refresh');
    }
    aggErrors.kickoffAggAction(dispatch, aggAction, institutionLogin, location);
  };

  const showActionButton =
    !hideAction && (!fiIssue || fiIssue.issueType === 'PARTIAL_OUTAGE' || fiIssue.issueType === 'SCHEDULED_OUTAGE');

  const onManageUpdatesClick = useCallback(
    () => showManageUpdatesDialog({ defaultValue: institutionLogin.id }),
    [institutionLogin.id],
  );

  const isRefreshFrequencySupported = useAppSelector((state) =>
    institutionLoginsSelectors.isRefreshFrequencySupported(state, institutionLogin?.id || ''),
  );

  const isPauthInvalidToken = institutionLoginsUtils.isPauthInvalidToken(aggAction.status);

  return (
    <Stack direction='row' spacing={1} alignItems='center'>
      {isRefreshFrequencySupported && !isPauthInvalidToken && (
        <Button size='small' id='manage-institution-login-updates' variant='text' onClick={onManageUpdatesClick}>
          Manage updates
        </Button>
      )}

      {aggLoading && showActionButton ? (
        <CircularProgress size={32} sx={{ marginY: 4, alignSelf: 'center' }} />
      ) : (
        <Stack direction={'row'} gap={2}>
          {showActionButton ? (
            <Button size='small' id={'accounts-fix-account-button'} variant='contained' onClick={kickoffReconnect}>
              {!fiIssue ? aggAction.actionLabel : 'Try again anyway'}
            </Button>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
};

/** This banner is used to provide status & errors for an institution login */
export const InstitutionLoginStatusBanner = ({
  institutionLoginId,
  location,
  dismissible,
  showBoth,
  showA2AEntry,
  hideA2AOnVerified,
  onlyA2AOnVerified,
  account,
  hideMigrationAction = false,
}: InstitutionLoginStatusBannerProps) => {
  const shownTracked = useRef(false);

  const statusBannerVisibility = useAppSelector((state) =>
    aggErrorsSelectors.getStatusBannerVisibilityForInstitutionLogin(state, institutionLoginId, !dismissible),
  );
  const institutionLogin = statusBannerVisibility?.institutionLogin;
  const fiIssue = statusBannerVisibility?.fiIssue;
  const aggAction: AggAction | undefined = statusBannerVisibility?.aggAction || undefined;
  const isAggError = Boolean(statusBannerVisibility?.isAggError && aggAction && institutionLogin);

  const institution = useAppSelector((state) =>
    institutionLogin ? institutionsSelectors.getInstitution(state, institutionLogin.id) : undefined,
  );

  const trackAction = useCallback(
    (action: 'close' | 'refresh' | 'shown') => {
      if (fiIssue) {
        tracker.track(tracker.events.fiStatusBanner, {
          type: fiIssue.issueType,
          source: fiIssue.issueSource,
          channel: fiIssue.channel,
          location,
          institution_id: institutionLogin?.institutionId,
          institution_name: institution?.name || 'Unknown',
          aggregator: institution?.aggregator || 'Unknown',
          action,
        });
      }
    },
    [fiIssue, institution?.aggregator, institution?.name, institutionLogin?.institutionId, location],
  );

  const dispatch = useAppDispatch();
  const removeFiStatusUntilNextSession = useCallback(() => {
    if (fiIssue && institutionLogin) {
      trackAction('close');
      dispatch(institutionLoginsActions.addDismissedFiIssue({ id: institutionLogin.id, type: fiIssue.issueType }));
    }
  }, [dispatch, fiIssue, institutionLogin, trackAction]);

  useEffect(() => {
    // only track shown the first time we see an fiIssue
    if (!shownTracked.current && fiIssue) {
      trackAction('shown');
      shownTracked.current = true;
    }
  }, [fiIssue, trackAction]);

  const migrationActionHandled = hideMigrationAction && fiMigrationUtils.isFiMigrationAction(aggAction);
  const effectiveAggError = isAggError && !migrationActionHandled;
  const shouldShowBoth = effectiveAggError && aggAction && fiIssue && (showBoth || fiIssue.issueType === 'INFO');
  const showInfoOnly = !isAggError && institutionLogin && fiIssue && fiIssue.issueType === 'INFO';

  let ret: ReactNode;

  const showFloatingDismiss = Boolean(fiIssue && dismissible && institutionLogin);
  const theme = useTheme();
  const floatingDismissButton = showFloatingDismiss ? (
    <IconButton
      size='small'
      variant='outlined'
      aria-label='dismiss'
      id='fi-status-dismiss'
      onClick={removeFiStatusUntilNextSession}
      sx={{
        position: 'absolute',
        top: `-${theme.spacing(1)}`,
        right: `-${theme.spacing(1)}`,
        zIndex: 1,
        boxShadow: theme.shadows[12],
      }}
    >
      <CloseRoundedIcon fontSize='inherit' />
    </IconButton>
  ) : null;

  const renderStatusNotice = (body: ReactNode, issue?: FiIssue): ReactNode => {
    if (!aggAction || !institutionLogin) return null;
    return (
      <Box sx={{ position: 'relative' }}>
        {floatingDismissButton}
        <Paper>
          <Alert
            icon={WarningIcon}
            severity={issue?.issueType === 'INFO' ? 'info' : 'warning'}
            action={
              <FiLoginActionButton
                institutionLogin={institutionLogin}
                fiIssue={issue}
                aggAction={aggAction}
                location={location}
                trackAction={trackAction}
                hideAction={migrationActionHandled}
              />
            }
            sx={alertStyles}
          >
            {body}
          </Alert>
        </Paper>
      </Box>
    );
  };

  if (shouldShowBoth && aggAction && institutionLogin) {
    ret = (
      <Stack gap={2}>
        <Box sx={{ position: 'relative' }}>
          {floatingDismissButton}
          <Paper>
            <Alert
              severity='info'
              sx={alertStyles}
              action={
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <FiLoginActionButton
                    institutionLogin={institutionLogin}
                    fiIssue={fiIssue}
                    aggAction={aggAction}
                    location={location}
                    trackAction={trackAction}
                  />
                </Box>
              }
            >
              <FiIssueBody fiIssue={fiIssue} />
            </Alert>
          </Paper>
        </Box>
        <Paper>
          <Alert
            icon={WarningIcon}
            severity='warning'
            sx={alertStyles}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <FiLoginActionButton
                  institutionLogin={institutionLogin}
                  aggAction={aggAction}
                  location={location}
                  trackAction={trackAction}
                />
              </Box>
            }
          >
            <FiErrorBody aggAction={aggAction} />
          </Alert>
        </Paper>
      </Stack>
    );
  } else if (isAggError && fiIssue) {
    ret = renderStatusNotice(<FiIssueBody fiIssue={fiIssue} />, fiIssue);
  } else if (effectiveAggError && aggAction) {
    ret = renderStatusNotice(<FiErrorBody aggAction={aggAction} />);
  } else if (showInfoOnly) {
    ret = (
      <Alert icon={!fiIssue ? WarningIcon : undefined} severity={fiIssue ? 'info' : 'warning'} sx={alertStyles}>
        <FiIssueBody fiIssue={fiIssue} />
      </Alert>
    );
  } else if (showA2AEntry && account && institutionLogin) {
    ret = (
      <A2AEntryButton
        account={account}
        institutionLogin={institutionLogin}
        hideOnVerified={hideA2AOnVerified}
        onlyShowVerified={onlyA2AOnVerified}
      />
    );
  } else {
    ret = null;
  }

  return ret;
};

type InstitutionStatusBannerProps = {
  institutionId: string;
  className?: string;
};

/** This banner is used to provide status for an entire institution  */
export const InstitutionStatusBanner = ({ institutionId }: InstitutionStatusBannerProps) => {
  const institution = useAppSelector((state) => institutionsSelectors.getInstitution(state, institutionId));

  const fiIssue = institution?.fiIssue;

  useEffectOnce(() => {
    if (fiIssue) {
      tracker.track(tracker.events.fiStatusBanner, {
        type: fiIssue.issueType,
        source: fiIssue.issueSource,
        channel: fiIssue.channel,
        location: 'addFi authenticator',
        institution_id: institution.id,
        institution_name: institution.name || 'Unknown',
        aggregator: institution.aggregator || 'Unknown',
        action: 'shown',
      });
    }
  });

  return fiIssue ? (
    <Alert severity='info' sx={{ marginY: 1 }}>
      <FiIssueBody fiIssue={fiIssue} />
    </Alert>
  ) : null;
};

/** This banner is used for Apple Wallet Institution Logins */
export const AppleWalletErrorBanner = ({ institutionLoginId }: { institutionLoginId?: string }) => {
  const statusBannerVisibility = useAppSelector((state) =>
    aggErrorsSelectors.getStatusBannerVisibilityForInstitutionLogin(state, institutionLoginId || '', false),
  );
  const institutionLogin = statusBannerVisibility?.institutionLogin;
  const showWarning = statusBannerVisibility?.appleWalletShowWarning;
  const showAppleWalletBanner = statusBannerVisibility?.appleWalletVisible;

  const lastRefreshSuccessfulAt = institutionLogin?.aggregators?.first()?.lastRefreshSuccessfulAt;
  const lastSyncAt = lastRefreshSuccessfulAt ? DateTime.fromISO(lastRefreshSuccessfulAt) : null;

  const lastSyncText = lastSyncAt
    ? `Last synced: ${lastSyncAt.toFormat("HH:mm:ss 'on' MMM dd yyyy")}. You can sync the account using your iPhone.`
    : '';

  if (!showAppleWalletBanner) {
    return null;
  }

  return (
    <Alert icon={!showWarning ? WarningIcon : undefined} severity={showWarning ? 'info' : 'warning'} sx={alertStyles}>
      <AlertTitle variant='paragraphMedium'>
        {showWarning ? 'Re-sync with Apple Wallet' : 'Apple Wallet access needed'}
      </AlertTitle>
      <Typography variant='paragraphRegular' color='text.secondary'>
        {showWarning
          ? lastSyncText
          : 'There was an issue connecting to Apple Wallet. Re-connect the account using your iPhone.'}
      </Typography>
    </Alert>
  );
};
